import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  OrganisationTypeConstants,
  ProjectGroupPermission,
  ProjectPermissionConstants,
  ProjectTypeConstants,
  UserType,
} from "../../../constants";
import { FileType, SelectData, TabsList } from "../../../models";
import { Origin } from "../../../models/shared/origin";
import { IStateOrigin } from "../../../navigators/navigateStateObjects";
import {
  GetGroupDetailsResponse as GetPublicGroupDetailsResponse,
  getProjectAggregate as getPublicProjectAggregate,
  searchProjects,
} from "../../../service/publicquery";
import { getAggregateIssuances, GetGroupDetailsResponse, getProjectAggregate } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { distinctBy, moveCarbonToFront, useDealQuickAction } from "../../../utils";
import {
  getLandholdingDashboardRoute,
  getLandholdingDocumentsRoute,
  getLandholdingInventoryRoute,
  getLandholdingLocationRoute,
  getLandholdingTasksRoute,
  getManageLandholdingRoute,
} from "../../../utils/routes";
import { ChartData, dataGridMapFilterCriteria, ProfileMenuItemType, Toast } from "../../../widget";
import { LandholdingContext } from "./LandholdingContext";

interface UseLandholdingReturnData {
  landholdingUuid: string | undefined;
  landholdingDetails: GetGroupDetailsResponse | undefined;
  publicLandholdingDetails: GetPublicGroupDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
  projectsChartData: ChartData;
  areaChartData: ChartData;
  projectsChartColours: string[][];
  areaChartColours: string[][];
  isInventoryChartLoading: boolean;
  inventoryChartData: ChartData;
  /** The statuses actually applied - forced to Issued only without full access to the projects. */
  selectedStatuses: string[];
  handleStatusFilter: (values: string[]) => void;
  /** Whether the user can read every project on this landholding. */
  hasFullProjectAccess: boolean;
  selectedUnitMeasurementData: string;
  updateInventoryChartData: (unitMeasurement: string) => void;
  unitMeasurementDropDownData: SelectData;
  currentUserType?: UserType;
  readGroupProjectsPermission: ProjectGroupPermission | undefined;
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: (show: boolean) => void;
  publicProjectsDevelopers: {
    name?: string;
    icon?: string;
  }[];
}

/** Fixed key so a repeatedly failing fetch replaces its own toast rather than stacking. */
const INVENTORY_LOAD_ERROR_TOAST_KEY = "landholding-inventory-load-error";

/*
  Unlike the inventory management board, this surface deliberately does NOT hide Ecosystem recovery
  from the measurement dropdown. Carbon is pulled to the front so it sits directly under Overall.
*/
export const buildLandholdingMeasurementDropDownData = (measurementTypes: string[]): SelectData => {
  const dropDownSelectData: SelectData = [
    { key: "", value: "Overall" },
    ...measurementTypes.map((value: string) => ({
      key: value,
      value,
    })),
  ];

  return moveCarbonToFront(dropDownSelectData);
};

export const useLandholding = (): UseLandholdingReturnData => {
  const navigate = useNavigate();
  const {
    landholdingUuid,
    landholdingDetails,
    publicLandholdingDetails,
    readGroupProjectsPermission,
    hasGroupPermission,
    selectedStatuses,
    setSelectedStatuses,
    selectedUnitMeasurement: selectedUnitMeasurementData,
    setSelectedUnitMeasurement,
  } = useContext(LandholdingContext);

  const { currentUserType } = useAuth();
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const [projectsChartData, setProjectsChartData] = useState<ChartData>({});
  const [areaChartData, setAreaChartData] = useState<ChartData>({});
  const [inventoryChartData, setInventoryChartData] = useState<ChartData>({});
  /** Identifies the newest inventory fetch, so a slower earlier one cannot overwrite its result. */
  const inventoryRequestId = useRef(0);

  const hasFullProjectAccess = readGroupProjectsPermission === ProjectGroupPermission.FULL;
  /* A string, so it can be a `fetchInventoryData` dependency without a new array identity each
     render re-creating the callback and re-running the effect that calls it. */
  const statusFilterValue = selectedStatuses.join(",");
  const [projectsChartColours, setProjectsChartColours] = useState<string[][]>([]);
  const [areaChartColours, setAreaChartColours] = useState<string[][]>([]);
  const [unitMeasurementDropDownData, setUnitMeasurementDropDownData] = useState<SelectData>([]);
  const [isInventoryChartLoading, setIsInventoryChartLoading] = useState(true);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [publicProjectsDevelopers, setPublicProjectsDevelopers] = useState<
    {
      name?: string;
      icon?: string;
    }[]
  >([]);

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    backButtonText = "Back";
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    navigate(-1);
  };

  const projectFilterValue = `[${(landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [])
    .map((project) => project.uuid)
    .join(",")}]`;
  const projectsSignature = (landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [])
    .map((project) => `${project.uuid}:${project.projectType.displayName}`)
    .join("|");

  const fetchInventoryData = useCallback(
    async (unitMeasurement: string): Promise<void> => {
      /*
        The measurement and status selections can both change again before a request lands, so only
        the newest may touch the card — an older response would otherwise overwrite the figure and
        the measurement options for a selection the user has already moved on from.
      */
      inventoryRequestId.current += 1;
      const requestId = inventoryRequestId.current;
      const isCurrentRequest = (): boolean => requestId === inventoryRequestId.current;

      setIsInventoryChartLoading(true);
      const filterCriteria = dataGridMapFilterCriteria([]);
      const filters = [];

      if (unitMeasurement) {
        filters.push({
          filterBy: "measurementType",
          filterOperator: "eq",
          filterValue: unitMeasurement,
        });
      }

      if (statusFilterValue) {
        filters.push({
          filterBy: "issuanceStatus",
          filterOperator: "in",
          filterValue: `[${statusFilterValue}]`,
        });
      }

      filters.push({
        filterBy: "project",
        filterOperator: "in",
        filterValue: projectFilterValue,
      });

      await getAggregateIssuances({
        filters,
        groupBy: [landholdingDetails ? "projectNameMeasurement" : "verifiedOrPending"],
        isReadableProject: false,
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 1000,
        },
        filter: {
          results: {
            ...filterCriteria,
          },
        },
      })
        .then((response) => {
          if (!isCurrentRequest()) {
            return;
          }
          if (response.status === Status.Success && response.data) {
            if (landholdingDetails) {
              const { measurementTypes } = response.data.aspects;
              setUnitMeasurementDropDownData(buildLandholdingMeasurementDropDownData(measurementTypes));
              setInventoryChartData(
                response.data.results.reduce(
                  (previous, current) => ({
                    Available: previous.Available + current.availableUnits,
                    Reserved: previous.Reserved + current.reservedUnits,
                    Sold: previous.Sold + current.soldUnits,
                  }),
                  { Available: 0, Reserved: 0, Sold: 0 }
                )
              );
            } else {
              setInventoryChartData(
                response.data.results.reduce<ChartData>((previous, current) => {
                  // eslint-disable-next-line no-param-reassign
                  previous[current.groupByFieldContent1 ?? "Undefined"] =
                    current.soldUnits + current.reservedUnits + current.availableUnits;
                  return previous;
                }, {})
              );
            }
          } else {
            /*
              A rejected request must not leave the previous measurement's figure on the card — the
              server's `measurementType` filter only accepts a fixed set, so selecting a measurement
              outside it fails and the card would otherwise keep showing the last good number.
            */
            setInventoryChartData({});
            Toast.error({
              key: INVENTORY_LOAD_ERROR_TOAST_KEY,
              message: "Failed to load inventory for this landholding",
            });
          }
        })
        .finally(() => {
          // Only the newest request may clear the spinner; an older one finishing would hide the
          // fact that the current selection is still loading.
          if (isCurrentRequest()) {
            setIsInventoryChartLoading(false);
          }
        });
    },
    [projectFilterValue, statusFilterValue]
  );

  const fetchProjects = useCallback(async (): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.landholdings = {
      some: {
        groupUuid: {
          operator: "eq",
          value: landholdingUuid,
        },
      },
    };

    await searchProjects({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      /* eslint-enable */
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.status === Status.Success && response.data) {
        setPublicProjectsDevelopers(
          distinctBy(
            response.data.results
              .map((project) => ({
                name: project.developer?.displayName ?? undefined,
                icon: project.developer?.files.find((file) => file.type === FileType.SmallLogo)?.file.url,
              }))
              .filter((developer): developer is { name: string; icon: string | undefined } => !!developer.name),
            (developer) => developer.name
          )
        );
      }
    });
  }, [landholdingUuid]);

  const updateInventoryChartData = useCallback(
    (unitMeasurement: string): void => {
      setSelectedUnitMeasurement(unitMeasurement);
      fetchInventoryData(unitMeasurement);
    },
    [fetchInventoryData]
  );

  // Seed only: deals raised from a landholding page are associated with the landholding itself.
  const dealQuickAction = useDealQuickAction(
    currentUserType === OrganisationTypeConstants.ASSET_MANAGER && landholdingUuid
      ? {
          uuid: landholdingUuid,
          type: "Landholding",
          name: landholdingDetails?.groupName ?? publicLandholdingDetails?.groupName,
        }
      : undefined,
    () => Toast.error({ message: "Unable to check for a deal against this landholding" })
  );

  const quickActionsMenuOptionsList = useMemo<ProfileMenuItemType[]>(() => {
    const options: ProfileMenuItemType[] = [];

    if (hasGroupPermission(ProjectPermissionConstants.MANAGE_GROUP)) {
      options.push({
        id: options.length + 1,
        value: "Manage landholding",
        action: () => {
          navigate(
            getManageLandholdingRoute(landholdingUuid || "", currentUserType, `origin=${Origin.LandholdingDetailsPage}`)
          );
        },
      });
    }

    if (dealQuickAction) {
      options.push({ id: options.length + 1, ...dealQuickAction });
    }

    return options;
  }, [currentUserType, landholdingUuid, hasGroupPermission, navigate, dealQuickAction]);

  useEffect(() => {
    if (!landholdingDetails?.groupGuid && !publicLandholdingDetails?.groupGuid) {
      return;
    }

    const loadDashboardData = async (): Promise<void> => {
      const projects = landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [];
      const areaAggregateResponse = landholdingDetails
        ? await getProjectAggregate({
            filterBy: "group",
            filterOperator: "eq",
            filterValue: (landholdingDetails?.groupGuid ?? publicLandholdingDetails?.groupGuid) as string,
            groupBy: "projectType",
            aggregation: "sum",
            aggregate: "projectAreaHa",
          })
        : await getPublicProjectAggregate({
            filterBy: "group",
            filterOperator: "eq",
            filterValue: publicLandholdingDetails?.groupGuid as string,
            groupBy: "projectType",
            aggregation: "sum",
            aggregate: "projectAreaHa",
          });

      if (areaAggregateResponse.status === Status.Success && areaAggregateResponse.data) {
        setAreaChartData(
          areaAggregateResponse.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = current.value;
            return previous;
          }, {})
        );
        setAreaChartColours(
          areaAggregateResponse.data.map((project) => ProjectTypeConstants.PROJECT_TYPE_CHART_COLOR_MAP[project.key])
        );
      }

      if (projects.length > 0) {
        setProjectsChartData(
          projects.reduce<ChartData>((previous, current) => {
            const projectType = current.projectType.displayName;
            // eslint-disable-next-line no-param-reassign
            previous[projectType] = (previous[projectType] || 0) + 1;
            return previous;
          }, {})
        );

        setProjectsChartColours(
          projects.reduce((accumulator: string[][], project) => {
            const color = ProjectTypeConstants.PROJECT_TYPE_CHART_COLOR_MAP[project.projectType.displayName];
            if (!accumulator.includes(color)) {
              accumulator.push(color);
            }
            return accumulator;
          }, [])
        );
      } else {
        setProjectsChartData({});
        setProjectsChartColours([]);
      }

      // We need the extra data we get from this api in some places in the FE
      if (publicLandholdingDetails) {
        await fetchProjects();
      }

      await fetchInventoryData(selectedUnitMeasurementData);
    };

    loadDashboardData().catch(() => {
      setIsInventoryChartLoading(false);
    });
  }, [
    fetchInventoryData,
    landholdingDetails?.groupGuid,
    publicLandholdingDetails?.groupGuid,
    projectsSignature,
    selectedUnitMeasurementData,
  ]);

  const tabsList = useMemo((): TabsList => {
    const tabState = stateOrigin
      ? {
          origin: stateOrigin,
        }
      : undefined;

    return landholdingDetails
      ? [
          {
            label: "Details",
            route: getLandholdingDashboardRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Location",
            route: getLandholdingLocationRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Inventory",
            route: getLandholdingInventoryRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Documents",
            route: getLandholdingDocumentsRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Tasks",
            route: getLandholdingTasksRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
        ]
      : [
          {
            label: "Details",
            route: getLandholdingDashboardRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Inventory",
            route: getLandholdingInventoryRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Documents",
            route: getLandholdingDocumentsRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
        ];
  }, [landholdingUuid, currentUserType, stateOrigin]);

  return {
    landholdingUuid,
    landholdingDetails,
    publicLandholdingDetails,
    handleBackClick,
    tabsList,
    backButtonText,
    projectsChartData,
    areaChartData,
    projectsChartColours,
    areaChartColours,
    isInventoryChartLoading,
    inventoryChartData,
    selectedStatuses,
    handleStatusFilter: setSelectedStatuses,
    hasFullProjectAccess,
    selectedUnitMeasurementData,
    updateInventoryChartData,
    unitMeasurementDropDownData,
    currentUserType,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,
    publicProjectsDevelopers,
    readGroupProjectsPermission,
  };
};
